import React, { SyntheticEvent, useCallback, useMemo } from 'react';
import { SiteChoice, SiteOption } from '../types';
import { useIntl } from 'react-intl';
import { SurfaceAutocomplete } from '../../../components/design-system';

export const NONE_SITE_CHOICE: SiteChoice = { id: null, name: 'None' };

function compareSites(option: SiteChoice, value: SiteChoice): boolean {
  return option.id === value.id;
}

interface SiteSelectProps {
  disabled?: boolean;
  label: string;
  placeholder?: string;
  sites: SiteOption[];
  value: SiteChoice | null;
  allowUnselected?: boolean;
  onChange: (value: SiteChoice | null) => void;
}

export const SiteSelect = React.memo(
  ({
    disabled = false,
    label,
    placeholder,
    sites,
    value,
    allowUnselected = false,
    onChange,
  }: SiteSelectProps) => {
    const intl = useIntl();
    const options = useMemo<SiteChoice[]>(() => [NONE_SITE_CHOICE, ...sites], [
      sites,
    ]);
    const inputLabelProps = useMemo(
      () => (placeholder ? { shrink: true } : undefined),
      [placeholder]
    );
    const handleChange = useCallback(
      (_event: SyntheticEvent, option: SiteChoice | null) => onChange(option),
      [onChange]
    );
    const getSiteLabel = useCallback(
      (option: SiteChoice) =>
        option.id === null
          ? intl.formatMessage({
              id: 'general.none',
              defaultMessage: 'None',
            })
          : option.name,
      [intl]
    );
    const textFieldProps = useMemo(
      () => ({ InputLabelProps: inputLabelProps, label, placeholder }),
      [inputLabelProps, label, placeholder]
    );

    return (
      <SurfaceAutocomplete
        disableClearable={!allowUnselected}
        disabled={disabled}
        getOptionLabel={getSiteLabel}
        isOptionEqualToValue={compareSites}
        onChange={handleChange}
        options={options}
        surface="sunken"
        textFieldProps={textFieldProps}
        value={value}
      />
    );
  }
);
